import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const field = "border-white/15 bg-white/5 text-white placeholder:text-white/40";

export default function LoginPage() {
  const { login, loginFirst, user, loading, ended } = useAuth();
  const nav = useNavigate();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  // เข้าครั้งแรก (Ben 2026-10-15): the right first password opens a "set your own" step right here — the typed
  // current password stays in this component's memory only, never in storage.
  const [first, setFirst] = useState(false);
  // a customer / dealer login of this web (not SQ2.0): its new password is for this web only
  const [localFirst, setLocalFirst] = useState(false);
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Already signed in (90-day session, Ben 2026-10-13): wait for /api/me, then straight in — no form flash.
  if (loading) return null;
  if (user) {
    queueMicrotask(() => nav("/", { replace: true }));
    return null;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(u.trim(), p);
      nav("/", { replace: true });
    } catch (e: any) {
      if (e.code === "must_change") { setFirst(true); setLocalFirst(!!e.local); setN1(""); setN2(""); }
      else setErr(e.message || "เข้าสู่ระบบไม่สำเร็จ");
    } finally {
      setBusy(false);
    }
  }

  async function submitFirst(e: React.FormEvent) {
    e.preventDefault();
    if (n1.length < 6) return setErr("รหัสผ่านใหม่ต้องยาว 6 ตัวขึ้นไป");
    if (n1 !== n2) return setErr("รหัสใหม่สองช่องไม่ตรงกัน");
    setBusy(true);
    setErr(null);
    try {
      await loginFirst({ username: u.trim(), password: p, next: n1, confirm: n2 });
      nav("/", { replace: true });
    } catch (e: any) {
      setErr(e.message || "ตั้งรหัสไม่สำเร็จ");
      if (e.code === "bad_credentials") { setFirst(false); setP(""); }
    } finally {
      setBusy(false);
    }
  }

  const shown = err ?? (!first && ended?.error ? ended.error : null);

  return (
    <div className="grid min-h-screen place-items-center bg-beijer-dark bg-pinstripe p-4 text-white">
      <div className="absolute inset-0 -z-0 overflow-hidden opacity-25" aria-hidden>
        <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-accent-orange/40 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 h-[34rem] w-[34rem] rounded-full bg-beijer/50 blur-3xl" />
        <div className="absolute top-1/3 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-purple-500/20 blur-3xl" />
      </div>
      <div className="z-10 w-full max-w-md animate-fade-up">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/logos/beijer-bgrimm-white.png" alt="Beijer B Grimm" className="mb-5 h-12 w-auto" />
          <h1 className="font-display text-2xl font-black uppercase tracking-wider">
            <span className="bb-marker-open" aria-hidden />
            พอร์ทัลราคาขาย
            <span className="bb-marker-close" aria-hidden />
          </h1>
          <p className="mt-2 text-sm text-white/70">Beijer B Grimm Thailand · License · Wholesale · ราคาขายทั่วไป</p>
        </div>
        {first ? (
          <form onSubmit={submitFirst} className="rounded-xl border border-white/10 bg-white/5 p-6 shadow-panel backdrop-blur-md">
            <h2 className="text-lg font-bold">เข้าครั้งแรก — ตั้งรหัสผ่านของคุณเอง</h2>
            <p className="mt-1 text-xs text-white/60">ชื่อผู้ใช้ {u.trim().toLowerCase()}{localFirst ? "" : " · ใช้รหัสใหม่นี้ได้ทุกเว็บ BBT"}</p>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="n1" className="text-white/85">รหัสผ่านใหม่ (6 ตัวขึ้นไป)</Label>
              <Input id="n1" type="password" autoComplete="new-password" autoFocus required value={n1} onChange={(e) => setN1(e.target.value)} className={field} />
            </div>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="n2" className="text-white/85">พิมพ์อีกครั้ง</Label>
              <Input id="n2" type="password" autoComplete="new-password" required value={n2} onChange={(e) => setN2(e.target.value)} className={field} />
            </div>
            {shown && <p role="alert" className="mt-4 rounded-md border border-red-400/40 bg-red-500/15 px-3 py-2 text-sm text-red-100">{shown}</p>}
            <Button type="submit" variant="primary" size="lg" className="mt-6 w-full" disabled={busy}>
              {busy ? "กำลังบันทึก…" : "บันทึกและเข้าระบบ"}
            </Button>
            <button type="button" className="mt-3 w-full text-center text-xs text-white/60 underline underline-offset-2" onClick={() => { setFirst(false); setP(""); setErr(null); }}>
              ← กลับ
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="rounded-xl border border-white/10 bg-white/5 p-6 shadow-panel backdrop-blur-md">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-white/85">ชื่อผู้ใช้</Label>
              <Input id="username" autoFocus autoComplete="username" autoCapitalize="none" required value={u} onChange={(e) => setU(e.target.value)} className={field} />
            </div>
            <div className="mt-4 space-y-1.5">
              <Label htmlFor="password" className="text-white/85">รหัสผ่าน</Label>
              <Input id="password" type="password" autoComplete="current-password" required value={p} onChange={(e) => setP(e.target.value)} className={field} />
            </div>
            {shown && <p role="alert" className="mt-4 rounded-md border border-red-400/40 bg-red-500/15 px-3 py-2 text-sm text-red-100">{shown}</p>}
            <Button type="submit" variant="primary" size="lg" className="mt-6 w-full" disabled={busy}>
              {busy ? "กำลังเข้าสู่ระบบ…" : "เข้าสู่ระบบ"}
            </Button>
          </form>
        )}
        {/* One BBT login (Ben 2026-10-15) — same hint on every BBT web */}
        <div className="mt-5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs leading-relaxed text-white/75">
          <p className="mb-1 font-semibold text-white/90">เข้าครั้งแรก</p>
          <p>1 · ชื่อผู้ใช้ = ชื่อจริงภาษาอังกฤษ ตัวเล็ก (เช่น somchai)</p>
          <p>2 · รหัสแรก = ชื่อ + 1234 (เช่น somchai1234) → ระบบให้ตั้งรหัสใหม่ทันที</p>
          <p>3 · LINE: เพิ่มเพื่อน OA @046fxkhr แล้วพิมพ์ ชื่อ เว้นวรรค รหัส (เช่น somchai somchai1234)</p>
          <p className="mt-1 text-white/60">ชื่อ + รหัสเดียวกัน ใช้ได้ทุกเว็บ BBT · ลืมรหัส → แจ้งผู้จัดการ</p>
        </div>
      </div>
    </div>
  );
}
